import React, { Suspense } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "./lib/auth";
import { ConfirmProvider } from "./components/ConfirmDialog";
import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import MasterDashboard from "./pages/MasterDashboard";
import ClientPage from "./pages/ClientPage";
import { ImpersonationBanner } from "./components/ViewAsClientSwitcher";

// Lazy-loaded — these aren't needed on initial load, so we keep them out of the main bundle.
const SettingsPage    = React.lazy(() => import("./pages/AccountManagementPage"));
const InternalPage    = React.lazy(() => import("./pages/InternalPage"));
const AdminHomePage   = React.lazy(() => import("./pages/AdminHomePage"));
const TeamPage        = React.lazy(() => import("./pages/TeamPage"));
const ClientPaymentsPage = React.lazy(() => import("./pages/ClientPaymentsPage"));
const ClientDetailsPage  = React.lazy(() => import("./pages/ClientDetailsPage"));
const ClientResourcesPage = React.lazy(() => import("./pages/ClientResourcesPage"));
const ClientRequirementsPage = React.lazy(() => import("./pages/ClientRequirementsPage"));
const ClientRequestsPage = React.lazy(() => import("./pages/ClientRequestsPage"));
const AdminRequestsPage  = React.lazy(() => import("./pages/AdminRequestsPage"));
const AdminRequirementTemplatesPage = React.lazy(() => import("./pages/AdminRequirementTemplatesPage"));
const AdminKpisPage      = React.lazy(() => import("./pages/AdminKpisPage"));
const GlobalSearch    = React.lazy(() => import("./components/GlobalSearch"));
const Tutorial        = React.lazy(() => import("./components/Tutorial"));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [pathname]);
  return null;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "client" && user.client_id) return <Navigate to={`/clients/${user.client_id}`} replace />;
  if (user.role === "admin") return <Navigate to="/home" replace />;
  return <Navigate to="/clients" replace />;
}

function Fallback() {
  return <div className="min-h-screen flex items-center justify-center text-zinc-600 text-[12px]">Loading…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ImpersonationBanner />
          <Suspense fallback={null}>
            <GlobalSearch />
            <Tutorial />
          </Suspense>
          <Suspense fallback={<Fallback />}>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/change-password" element={
                <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
              } />
              <Route path="/home" element={
                <ProtectedRoute allow={["admin"]}><AdminHomePage /></ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute allow={["admin", "team", "account_manager"]}><MasterDashboard /></ProtectedRoute>
              } />
              <Route path="/dashboard" element={<Navigate to="/clients" replace />} />
              <Route path="/clients/:id" element={
                <ProtectedRoute><ClientPage /></ProtectedRoute>
              } />
              <Route path="/clients/:id/payments" element={
                <ProtectedRoute><ClientPaymentsPage /></ProtectedRoute>
              } />
              <Route path="/clients/:id/details" element={
                <ProtectedRoute><ClientDetailsPage /></ProtectedRoute>
              } />
              <Route path="/clients/:id/resources" element={
                <ProtectedRoute><ClientResourcesPage /></ProtectedRoute>
              } />
              <Route path="/clients/:id/requirements" element={
                <ProtectedRoute><ClientRequirementsPage /></ProtectedRoute>
              } />
              <Route path="/clients/:id/requests" element={
                <ProtectedRoute><ClientRequestsPage /></ProtectedRoute>
              } />
              <Route path="/admin/requests" element={
                <ProtectedRoute allow={["admin"]}><AdminRequestsPage /></ProtectedRoute>
              } />
              <Route path="/admin/requirement-templates" element={
                <ProtectedRoute allow={["admin"]}><AdminRequirementTemplatesPage /></ProtectedRoute>
              } />
              <Route path="/admin/kpis" element={
                <ProtectedRoute><AdminKpisPage /></ProtectedRoute>
              } />
              <Route path="/accounts" element={<Navigate to="/settings" replace />} />
              <Route path="/settings" element={
                <ProtectedRoute allow={["admin", "team", "account_manager"]}><SettingsPage /></ProtectedRoute>
              } />
              <Route path="/tasks" element={<Navigate to="/home" replace />} />
              <Route path="/posts" element={<Navigate to="/home" replace />} />
              <Route path="/team" element={
                <ProtectedRoute allow={["admin"]}><TeamPage /></ProtectedRoute>
              } />
              <Route path="/cadences" element={
                <ProtectedRoute allow={["admin", "team", "account_manager"]}><InternalPage /></ProtectedRoute>
              } />
              <Route path="/internal" element={<Navigate to="/cadences" replace />} />
              <Route path="/team" element={<Navigate to="/dashboard" replace />} />
              <Route path="/team/:teamId" element={<Navigate to="/dashboard" replace />} />
              <Route path="/analytics" element={<Navigate to="/settings?tab=analytics" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}
